import React, { useState, useEffect, useMemo } from 'react'
import { Grid } from '@mui/material'
import SectionAccordion from 'gui/components/accordion/section-accordion'
import MuiTextFieldWrapper from 'gui/components/common/MuiTextField'
import MuiCheckbox from 'gui/components/common/MuiCheckbox'
import type { ContentInfoChanges } from './types'
import { useAiInjectedMetadata } from './useAiInjectedMetadata'
import './course-content-information-shared.scss'

interface SeoContentData {
  id?: string
  seoTitle?: string | null
  seoDescription?: string | null
  seoKeywords?: string | null
  seoRobotsNoFollow?: boolean | null
  seoRobotsNoIndex?: boolean | null
}

interface VideoContentSeoSectionProps {
  contentData: SeoContentData | null
  isViewOnly: boolean
  onDirtyChange: (isDirty: boolean) => void
  onChangesUpdate: (changes: ContentInfoChanges | null) => void
  onResetTriggered?: number
  /** SEO field names → values injected by another component (e.g. AI Metadata Generation). */
  aiInjectedMetadata?: Record<string, unknown> | null
}

const SEO_FIELDS = [
  'seoTitle',
  'seoDescription',
  'seoKeywords',
  'seoRobotsNoFollow',
  'seoRobotsNoIndex',
] as const

type SeoFormValues = {
  seoTitle: string
  seoDescription: string
  seoKeywords: string
  seoRobotsNoFollow: boolean
  seoRobotsNoIndex: boolean
}

const initialFormValues: SeoFormValues = {
  seoTitle: '',
  seoDescription: '',
  seoKeywords: '',
  seoRobotsNoFollow: false,
  seoRobotsNoIndex: false,
}

/**
 * Video Content SEO Section
 * Parent-controlled section: reports dirty state and changes up; parent runs contentUpdate mutation.
 */
const SEO_APPLY_KEYS = ['seoTitle', 'seoDescription', 'seoKeywords'] as const

function VideoContentSeoSection ({
  contentData,
  isViewOnly,
  onDirtyChange,
  onChangesUpdate,
  onResetTriggered,
  aiInjectedMetadata,
}: VideoContentSeoSectionProps) {
  const id = contentData?.id

  const [formValues, setFormValues] = useState<SeoFormValues>(initialFormValues)
  const [initialValues, setInitialValues] = useState<SeoFormValues>(initialFormValues)

  useEffect(() => {
    if (contentData && id) {
      const values: SeoFormValues = {
        seoTitle: contentData.seoTitle ?? '',
        seoDescription: contentData.seoDescription ?? '',
        seoKeywords: contentData.seoKeywords ?? '',
        seoRobotsNoFollow: contentData.seoRobotsNoFollow ?? false,
        seoRobotsNoIndex: contentData.seoRobotsNoIndex ?? false,
      }
      setFormValues(values)
      setInitialValues(values)
    }
  }, [contentData, id])

  useAiInjectedMetadata({
    aiInjectedMetadata,
    allowedKeys: SEO_APPLY_KEYS,
    onApply: (updates) => setFormValues((prev) => ({ ...prev, ...updates }) as SeoFormValues),
  })

  const isDirty = useMemo(() => {
    if (!id) return false
    return SEO_FIELDS.some((key) => formValues[key] !== initialValues[key])
  }, [formValues, initialValues, id])

  useEffect(() => {
    onDirtyChange(isDirty)
  }, [isDirty, onDirtyChange])

  const changedValues = useMemo((): ContentInfoChanges | null => {
    if (!id || !isDirty) return null

    const contentInput: ContentInfoChanges['contentInput'] = { id }

    SEO_FIELDS.forEach((field) => {
      const current = formValues[field]
      const initial = initialValues[field]
      if (current !== initial) {
        const value = current === '' ? null : current
        ;(contentInput as Record<string, unknown>)[field] = value
      }
    })

    return {
      contentInput,
      generatePath: false,
    }
  }, [id, formValues, initialValues, isDirty])

  useEffect(() => {
    onChangesUpdate(changedValues)
  }, [changedValues, onChangesUpdate])

  useEffect(() => {
    if (onResetTriggered !== undefined) {
      setFormValues({ ...initialValues })
    }
  }, [onResetTriggered, initialValues])

  const handleFieldChange =
    (field: keyof SeoFormValues) =>
    (
      event:
        | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
        | { target: { value: unknown; type?: string; checked?: boolean } },
    ) => {
      const { target } = event
      const value =
        target.type === 'checkbox' && 'checked' in target ? target.checked : target.value
      setFormValues((prev) => ({ ...prev, [field]: value }))
    }

  return (
    <SectionAccordion title="SEO" collapsed={true}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={6}>
          <MuiTextFieldWrapper
            className="content-detail__input"
            label="SEO Title"
            name="seoTitle"
            value={formValues.seoTitle}
            onChange={handleFieldChange('seoTitle')}
            disabled={isViewOnly}
            fullWidth
            variant="outlined"
          />
        </Grid>
      </Grid>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12}>
          <MuiTextFieldWrapper
            className="content-detail__input"
            label="SEO Description"
            name="seoDescription"
            value={formValues.seoDescription}
            onChange={handleFieldChange('seoDescription')}
            disabled={isViewOnly}
            fullWidth
            variant="outlined"
          />
        </Grid>
      </Grid>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12}>
          <MuiTextFieldWrapper
            className="content-detail__input"
            label="SEO Keywords (separated by commas)"
            name="seoKeywords"
            value={formValues.seoKeywords}
            onChange={handleFieldChange('seoKeywords')}
            disabled={isViewOnly}
            fullWidth
            variant="outlined"
          />
        </Grid>
      </Grid>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={4}>
          <MuiCheckbox
            name="seoRobotsNoFollow"
            label="SEO Robots - No Follow"
            onChange={handleFieldChange('seoRobotsNoFollow')}
            checked={formValues.seoRobotsNoFollow}
            disabled={isViewOnly}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={4}>
          <MuiCheckbox
            name="seoRobotsNoIndex"
            label="SEO Robots - No Index"
            onChange={handleFieldChange('seoRobotsNoIndex')}
            checked={formValues.seoRobotsNoIndex}
            disabled={isViewOnly}
          />
        </Grid>
      </Grid>
    </SectionAccordion>
  )
}

export default VideoContentSeoSection
